/**
 * Pure helpers for the "Create New Lead" search flow (PersonalDetails.jsx).
 *
 * Extracted so the branching decisions — active vs archived grouping, whether a
 * selected result should open the restore dialog vs autofill, how to interpret
 * the async restore response — can be unit-tested without mounting the whole
 * PersonalDetails component (which drags in redux, react-hook-form, MUI, router,
 * Dentally, etc.).
 */
import { generateReasonsLabel } from 'src/utils/getReferralData';

/** The BE placeholder returned when an archived lead has no recorded reason. */
const NO_REASON_PLACEHOLDER = 'reason not found';

/**
 * Normalize an archive reason for display: drop empty / nullish values and the
 * BE "Reason not found" placeholder so the UI just omits the reason chip.
 */
export function cleanArchiveReason(val) {
    const raw = (val || '').trim();
    if (!raw) return '';
    if (raw.toLowerCase() === NO_REASON_PLACEHOLDER) return '';
    return raw;
}

/**
 * Human label for an archive reason code ("NOT_INTERESTED" -> "Not interested").
 * Reuses generateReasonsLabel, the same formatter the archive pickers use. Values
 * that are not UPPER_SNAKE codes (free text) are returned unchanged; the BE
 * placeholder / empty values give ''.
 */
export function archiveReasonLabel(val) {
    const raw = cleanArchiveReason(val);
    if (!raw) return '';
    if (!/^[A-Z0-9_]+$/.test(raw)) return raw;
    return generateReasonsLabel(raw);
}

/**
 * groupForOption — MUI Autocomplete groupBy value for a create-lead option.
 * Order of checks matters: create footer > Dentally > archived > active.
 */
export function groupForOption(option) {
    if (!option) return 'Active Leads';
    if (option.__create__) return '';
    if (option.__dentally__) return 'Dentally Patients';
    if (option.is_archived) return 'Archived Leads';
    return 'Active Leads';
}

/**
 * Split raw CRM search results into active + archived buckets, preserving order.
 */
export function splitCrmResults(results) {
    const list = Array.isArray(results) ? results : [];
    return {
        activeLeads: list.filter((l) => !l?.is_archived),
        archivedLeads: list.filter((l) => l?.is_archived),
    };
}

/**
 * Decide what selecting an Autocomplete option means.
 * Returns one of: 'freeform' | 'create' | 'dentally' | 'restore' | 'active' | 'noop'.
 *   - 'restore' → an archived CRM lead was picked; caller opens the restore dialog
 *     (must NOT autofill).
 *   - 'active'  → an active CRM lead; caller autofills as before.
 */
export function classifySelection(val) {
    if (!val) return 'noop';
    if (typeof val === 'string') return 'freeform';
    if (val.__create__) return 'create';
    if (val.__dentally__) return 'dentally';
    if (val.is_archived) return 'restore';
    return 'active';
}

/**
 * Interpret the async restore response.
 * The BE restore endpoint is async: 200 => scheduled (queued ~5 min).
 * Returns one of: 'inFlight' | 'scheduled' | 'error'.
 *   - 'inFlight'  → single-flight no-op (double-click); keep dialog, do nothing.
 *   - 'scheduled' → 200; show scheduled toast, close dialog, clear search, NO navigate.
 *   - 'error'     → keep dialog open + show error toast.
 */
export function classifyRestoreResult(res) {
    if (res && res.__inFlight) return 'inFlight';
    // The BE answers 200 { already_active: true } when the lead was never
    // archived, or was restored already. Nothing is scheduled in that case, so
    // promising it "will appear shortly" would be a lie about a lead that is
    // active right now. Before this the same 200 was reported as 'scheduled'
    // and, worse, the BE used to 404 instead — an unclearable error on a lead
    // that needed no restoring at all (RCA 2026-08-28, thread 11928).
    if (res && res.status === 200 && res.data && res.data.already_active) return 'alreadyActive';
    if (res && res.status === 200) return 'scheduled';
    return 'error';
}

export const RESTORE_SCHEDULED_MESSAGE =
    'Lead restore has been scheduled. It should appear in active leads shortly.';

export const RESTORE_ALREADY_ACTIVE_MESSAGE =
    'That patient is already active — no restore was needed.';
